import { defineStore } from 'pinia'
import type { CreateTeamPayload, Team, TeamListItem } from '../api/teams.api'
import { teamsApi } from '../api/teams.api'

interface TeamsState {
  items: TeamListItem[]
  isLoading: boolean
  error: string | null
  currentTeam: Team | null
  currentTeamRole: TeamListItem['role'] | null
  initialKioskPin: string | null
}

export const useTeamsStore = defineStore('teams', {
  state: (): TeamsState => ({
    items: [],
    isLoading: false,
    error: null,
    currentTeam: null,
    currentTeamRole: null,
    initialKioskPin: null,
  }),

  getters: {
    isTeamMode: (state) => state.currentTeam !== null,
    canInvite: (state) => state.currentTeamRole === 'owner' || state.currentTeamRole === 'moderator',
    isOwner: (state) => state.currentTeamRole === 'owner',
  },

  actions: {
    async fetchTeams() {
      this.isLoading = true
      this.error = null
      try {
        this.items = await teamsApi.list()
      } catch (e: any) {
        this.error = e?.response?.data?.detail || e?.message || 'Failed to load teams'
        this.items = []
      } finally {
        this.isLoading = false
      }
    },

    async createTeam(payload: CreateTeamPayload): Promise<TeamListItem | null> {
      this.isLoading = true
      this.error = null
      try {
        const team = await teamsApi.create(payload)
        this.initialKioskPin = team.initial_kiosk_pin ?? null
        await this.fetchTeams()
        const created = this.items.find((t) => t.id === team.id) ?? null
        return created
      } catch (e: any) {
        this.error = e?.response?.data?.detail || e?.message || 'Failed to create team'
        return null
      } finally {
        this.isLoading = false
      }
    },

    async enterTeam(teamId: number | string) {
      this.isLoading = true
      this.error = null
      try {
        const team = await teamsApi.detail(teamId)
        let listItem = this.items.find((t) => t.id === team.id)
        if (!listItem) {
          await this.fetchTeams()
          listItem = this.items.find((t) => t.id === team.id)
        }
        const role = team.viewer_role ?? listItem?.role ?? null
        this.currentTeamRole = role
        // Staff members cannot use team admin UI (dashboard, invites, …).
        if (role === 'member') {
          this.currentTeam = null
          return
        }
        this.currentTeam = team
      } catch (e: any) {
        this.error = e?.response?.data?.detail || e?.message || 'Failed to load team'
        this.currentTeam = null
        this.currentTeamRole = null
      } finally {
        this.isLoading = false
      }
    },

    async leaveTeam(teamId: number | string) {
      this.error = null
      try {
        await teamsApi.leave(teamId)
        if (this.currentTeam?.id === Number(teamId)) {
          this.exitTeam()
        }
        await this.fetchTeams()
      } catch (e: any) {
        this.error = e?.response?.data?.detail || e?.message || 'Could not leave team'
        throw e
      }
    },

    exitTeam() {
      this.currentTeam = null
      this.currentTeamRole = null
    },

    clearInitialKioskPin() {
      this.initialKioskPin = null
    },
  },
})
